import React from 'react';
import PropTypes from 'prop-types';

import {Stack, Box, Button, screenSizeSmaller} from 'views/components';
import {TimezoneAutocomplete} from 'views/module';

import {Translate} from 'views/module';

import CalendarDisplay from './calendar/CalendarDisplay';
import CreateEvent from './event_manager/create/CreateEvent';
import ViewEvent from './event_manager/view/ViewEvent';
import RemoveEvent from './event_manager/view/RemoveEvent';
import ViewRecurrence from './event_manager/view/ViewRecurrence';
import RemoveRecurrence from './event_manager/view/RemoveRecurrence';
import ViewLiveIn from './event_manager/view/ViewLiveIn';
import RemoveLiveIn from './event_manager/view/RemoveLiveIn';
import RescheduleEvent from './event_manager/view/RescheduleEvent';
import ScheduleCost from './ScheduleSummary';

import momentTZ from 'moment-timezone';
import moment from 'moment';

const ClientCalendar = (props) => {
  const [rangeView, setRangeView] = React.useState();
  const [view, setView] = React.useState(screenSizeSmaller('md') ? 'month' : 'week' );

  const [model, setModel] = React.useState({});
  const [loadedMonths, setLoadedMonths] = React.useState(new Set());
  const [selectedTimezone, setSelectedTimezone] = React.useState(momentTZ.tz.guess());
  const [reload, setReload] = React.useState(0);

  const scrollContainerRef = React.useRef(null);
  const parentReload = props.refresh;
  const clientScheduleEvents = props.clientScheduleEvents;
  const clientPaymentPlan = props.clientPaymentPlan;
  const clientBackend = props.clientBackend;
  const clientData = props.clientData;

  // Load the hourly rate
  React.useEffect(() => {
    if (!clientPaymentPlan) {
      clientBackend.payment.loadClientHourlyRate();
    }
  }, [parentReload, clientPaymentPlan]);

  // Reload calendar when refresh button is triggered
  const isFirstRender = React.useRef(true);
  React.useEffect(() => {
    if (isFirstRender.current) {
      isFirstRender.current = false; // Skip the first run
      return;
    }
    // Reload calendar when refresh button is triggered
    reloadCalendar();
  }, [parentReload]);

  // Scroll to the bottom
  React.useEffect(() => {
    if (scrollContainerRef.current) {
      scrollContainerRef.current.scrollIntoView({alignToTop: true, behavior: 'smooth', block: 'start'});
    }
  }, [scrollContainerRef]);

  const reloadCalendar = () => {
    setLoadedMonths(new Set());
    setReload((e) => e + 1);
  };

  const loadMonthlyEvents = (monthsToLoad) => {
    // Filter months that haven't been loaded yet
    const unloadedMonths = monthsToLoad.filter(
        ({year, month}) => !loadedMonths.has(`${year}-${month}`),
    );
    // Mark these months as loaded
    setLoadedMonths((prevLoadedMonths) => {
      const updatedLoadedMonths = new Set(prevLoadedMonths);
      unloadedMonths.forEach(({year, month}) => updatedLoadedMonths.add(`${year}-${month}`));
      return updatedLoadedMonths;
    });

    if (unloadedMonths.length) {
      const months = unloadedMonths.map((e) => ({year: `${e.year}`, month: `${e.month + 1}`}));
      clientBackend.schedule.loadMonthlyEvents(months, true);
    }
  };

  const edits = {
    addEvent: (eventType, newEvent) => {
      const complete = () => {
        setModel({});
        reloadCalendar();
      };

      if (eventType === 'FIXED_EVENT') {
        const input = {
          info: newEvent.info,
          eventData: {
            startTime: moment.tz(newEvent.eventData.startTime.format('YYYY-MM-DDTHH:mm:ss'), newEvent.eventData.timezone).format(),
            endTime: momentTZ.tz(newEvent.eventData.endTime.format('YYYY-MM-DDTHH:mm:ss'), newEvent.eventData.timezone).format(),
            timezone: newEvent.eventData.timezone,
          },
          caregiverData: {
            caregiverRn: newEvent.caregiverData.caregiver.caregiverRn,
          },
        };

        if (newEvent.breaks.breakDuration > 0 && newEvent.breaks.breakCount > 0) {
          input.breaks = newEvent.breaks;
        }

        clientBackend.schedule.addFixedEvent(input, complete);
      } else if (eventType === 'RECURRENCE_EVENT') {
        const input = {
          info: newEvent.info,
          recurrenceData: {
            startDate: newEvent.recurrenceData.startDate.format('YYYY-MM-DD'),
            endDate: newEvent.recurrenceData.endDate?.format('YYYY-MM-DD'),
            startTime: newEvent.recurrenceData.startTime.format('HH:mm:ss'),
            endTime: newEvent.recurrenceData.endTime.format('HH:mm:ss'),
            timezone: newEvent.recurrenceData.timezone,
            freq: newEvent.recurrenceData.freq,
            interval: Number(newEvent.recurrenceData.interval),
            byweekday: newEvent.recurrenceData.byweekday,
            bymonthday: newEvent.recurrenceData.bymonthday,
            bymonth: newEvent.recurrenceData.bymonth,
          },
          caregiverData: {
            caregiverRn: newEvent.caregiverData.caregiver.caregiverRn,
          },
        };

        if (newEvent.breaks.breakDuration > 0 && newEvent.breaks.breakCount > 0) {
          input.breaks = newEvent.breaks;
        }

        clientBackend.schedule.addRecurrence(input, complete);
      } else if (eventType === 'LIVEIN_EVENT') {
        const input = {
          info: newEvent.info,
          liveInData: {
            startDate: newEvent.liveInData.startDate.format('YYYY-MM-DD'),
            endDate: newEvent.liveInData.endDate?.format('YYYY-MM-DD'),
            timezone: newEvent.liveInData.timezone,
            byweekday: newEvent.liveInData.byweekday,
          },
          caregiverData: {
            caregiverRn: newEvent.caregiverData.caregiver.caregiverRn,
          },
        };

        clientBackend.schedule.addLiveIn(input, complete);
      }
    },
    removeEvent: (event, text) => {
      const complete = () => {
        setModel({});
        reloadCalendar();
      };
      const input = {
        description: {
          blob: text,
          editorType: 'MARKDOWN',
        },
      };
      clientBackend.schedule.cancelEvent(event.eventRn, input, complete);
    },
    removeRecurrence: (recurrenceRn, text) => {
      const complete = () => {
        setModel({});
        reloadCalendar();
      };

      const deactivatedData = {
        description: {
          blob: text,
          editorType: 'MARKDOWN',
        },
      };
      clientBackend.schedule.deactivateRecurrence(recurrenceRn, deactivatedData, complete);
    },
    removeLiveIn: (liveInRn, text) => {
      const complete = () => {
        setModel({});
        reloadCalendar();
      };

      const deactivatedData = {
        description: {
          blob: text,
          editorType: 'MARKDOWN',
        },
      };
      clientBackend.schedule.deactivateLiveIn(liveInRn, deactivatedData, complete);
    },
    rescheduleEvent: (eventRn, reasonsForException, eventData) => {
      const complete = () => {
        setModel({});
        reloadCalendar();
      };
      clientBackend.schedule.rescheduleEvent(eventRn, eventData, reasonsForException, complete);
    },
  };

  const openDialogs = {
    close: () => {
      setModel({});
    },
    openEvent: (eventRn, event) => {
      setModel({action: 'OPEN_EVENT', eventRn, event});
    },
    deleteEvent: (eventRn, event) => {
      setModel({action: 'REMOVE_EVENT', eventRn, event});
    },
    openRecurrence: (recurrenceRn, event) => {
      setModel({action: 'OPEN_RECURRENCE', recurrenceRn, event});
    },
    deletedRecurrence: (recurrenceRn, event) => {
      setModel({action: 'REMOVE_RECURRENCE', recurrenceRn, event});
    },
    openLiveIn: (liveInRn, event) => {
      setModel({action: 'OPEN_LIVEIN', liveInRn, event});
    },
    deleteLiveIn: (liveInRn, event) => {
      setModel({action: 'REMOVE_LIVEIN', liveInRn, event});
    },
    rescheduleEvent: (eventRn, event, updatedTimes) => {
      setModel({
        action: 'RESCHEDULE_EVENT',
        event: event,
        updatedTimes: updatedTimes,
      });
    },
  };

  const popModelGenerator = React.useCallback(() => {
    let popModel = null;
    if (model.action === 'CREATE_EVENT') {
      popModel = <CreateEvent
        onClose={openDialogs.close}
        addEvent={edits.addEvent}
        clientData={clientData}
      />;
    } else if (model.action === 'OPEN_EVENT') {
      popModel = <ViewEvent
        onClose={openDialogs.close}
        eventRn={model.eventRn}
        event={model.event}
        openRecurrence={openDialogs.openRecurrence}
        openLiveIn ={openDialogs.openLiveIn}
        deleteEvent={openDialogs.deleteEvent}
      />;
    } else if (model.action === 'REMOVE_EVENT') {
      popModel = <RemoveEvent
        onClose={openDialogs.close}
        eventRn={model.eventRn}
        event={model.event}
        removeEvent={edits.removeEvent}
      />;
    } else if (model.action === 'OPEN_RECURRENCE') {
      popModel = <ViewRecurrence
        onClose={openDialogs.close}
        recurrenceRn={model.recurrenceRn}
        event={model.event}
        deleteRecurrence={openDialogs.deletedRecurrence}
      />;
    } else if (model.action === 'OPEN_LIVEIN') {
      popModel = <ViewLiveIn
        onClose={openDialogs.close}
        liveInRn={model.liveInRn}
        event={model.event}
        deleteLiveIn={openDialogs.deleteLiveIn}
      />;
    } else if (model.action === 'REMOVE_RECURRENCE') {
      popModel = <RemoveRecurrence
        onClose={openDialogs.close}
        recurrenceRn={model.recurrenceRn}
        event={model.event}
        deleteRecurrence={edits.removeRecurrence}
      />;
    } else if (model.action === 'REMOVE_LIVEIN') {
      popModel = <RemoveLiveIn
        onClose={openDialogs.close}
        liveInRn={model.liveInRn}
        event={model.event}
        deleteLiveIn={edits.removeLiveIn}
      />;
    } else if (model.action === 'RESCHEDULE_EVENT') {
      popModel = <RescheduleEvent
        closeModel={openDialogs.close}
        event={model.event}
        rescheduleEvent={edits.rescheduleEvent}
        updatedTimes={model.updatedTimes}
      />;
    }
    return popModel;
  }, [model]);

  return (
    <Stack
      sx={{
        p: 2,
      }}
      spacing={1}
      ref={scrollContainerRef}
    >
      {popModelGenerator()}
      <Stack direction='row' spacing={{md: 2, xs: 1}} justifyContent='space-between' alignItems='center'>
        <TimezoneAutocomplete
          selectedTimezone={selectedTimezone}
          onTimezoneChange={setSelectedTimezone}
        />
        <Button
          onClick={() => setModel({action: 'CREATE_EVENT'})}
          sx={{
            my: 2,
          }}
        >
          <Translate expression='client.ClientDetail.Scheduling.AddNewSchedule' />
        </Button>
      </Stack>
      <Box
        sx={{
          width: '100%',
        }}
      >
        <CalendarDisplay
          rangeView={rangeView}
          setRangeView={setRangeView}
          view={view}
          setView={setView}
          loadMonthlyEvents={loadMonthlyEvents}
          events={Object.values(clientScheduleEvents)}
          openEvent={openDialogs.openEvent}
          rescheduleEvent={openDialogs.rescheduleEvent}
          reload={reload}
          viewingTimezone={selectedTimezone}
        />
      </Box>
      <Box
        sx={{
          height: '100%',
          width: '100%',
        }}
      >
        <ScheduleCost
          rangeView={rangeView}
          view={view}
          events={clientScheduleEvents}
          clientPaymentPlan={clientPaymentPlan}
        />
      </Box>
    </Stack>
  );
};

ClientCalendar.propTypes = {
  refresh: PropTypes.number.isRequired,
  clientScheduleEvents: PropTypes.object.isRequired,
  clientBackend: PropTypes.object.isRequired,
  clientPaymentPlan: PropTypes.object,
  clientData: PropTypes.object.isRequired,
};
export default React.memo(ClientCalendar);
